import type { Breadcrumb } from '~/types/breadcrumb'

/**
 * Structured data for page types that had none (SEO review 22.10.2026): BreadcrumbList on every page with
 * breadcrumbs, Organization and WebSite on the front page, BlogPosting on articles.
 */

/** "Etusivu" first, like the home icon of the breadcrumb. The last crumb is the page itself: no URL needed. */
export function breadcrumbList(crumbs: Breadcrumb[], baseUrl: string) {
  const visible = crumbs.filter(c => c.value && c.value !== '?')
  if (!visible.length) return null
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      { '@type': 'ListItem', 'position': 1, 'name': 'Etusivu', 'item': `${baseUrl}/` },
      ...visible.map((crumb, i) => ({
        '@type': 'ListItem',
        'position': i + 2,
        'name': crumb.value,
        ...(i < visible.length - 1 && { item: `${baseUrl}/${crumb.slug.replace(/^\/+/, '')}` })
      }))
    ]
  }
}

/** Contact details as in the site footer (SiteFooter.vue), profiles as in its SocialMediaLinks.vue */
export function organization(baseUrl: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${baseUrl}/#organization`,
    'name': 'Tarvike.com',
    'legalName': 'Ranuan Tarvikekeskus Oy',
    'url': `${baseUrl}/`,
    'logo': `${baseUrl}/favicon/favicon-196.png`,
    'foundingDate': '1988',
    'email': 'asiakaspalvelu@tarvike.com',
    'contactPoint': [
      { '@type': 'ContactPoint', 'contactType': 'customer service', 'telephone': '+358407167228', 'email': 'asiakaspalvelu@tarvike.com', 'areaServed': 'FI', 'availableLanguage': 'fi' },
      { '@type': 'ContactPoint', 'contactType': 'sales', 'telephone': '+358207437000', 'areaServed': 'FI', 'availableLanguage': 'fi' }
    ],
    'sameAs': [
      'https://www.facebook.com/tarvikecom/',
      'https://www.instagram.com/tarvikecom/',
      'https://www.tiktok.com/@tarvike.com',
      'https://www.youtube.com/channel/UCY9AQwtLYv_e412-1y35_ig',
      'https://fi.linkedin.com/company/tarvikecom'
    ]
  }
}

/** The site search: /haku?q=… (pages/haku.vue) */
export function website(baseUrl: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    'name': 'Tarvike.com',
    'url': `${baseUrl}/`,
    'inLanguage': 'fi',
    'publisher': { '@id': `${baseUrl}/#organization` },
    'potentialAction': {
      '@type': 'SearchAction',
      'target': { '@type': 'EntryPoint', 'urlTemplate': `${baseUrl}/haku?q={search_term_string}` },
      'query-input': 'required name=search_term_string'
    }
  }
}

export interface ArticleData {
  title: string
  url: string
  description?: string
  image?: string
  datePublished?: string
  author?: string
}

export function blogPosting(article: ArticleData, baseUrl: string) {
  const published = article.datePublished && !Number.isNaN(Date.parse(article.datePublished))
    ? new Date(article.datePublished).toISOString()
    : undefined
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    'headline': article.title.slice(0, 110),
    'url': article.url,
    'mainEntityOfPage': article.url,
    'inLanguage': 'fi',
    ...(article.description && { description: article.description }),
    ...(article.image && { image: article.image }),
    ...(published && { datePublished: published }),
    'author': article.author
      ? { '@type': 'Person', 'name': article.author }
      : { '@type': 'Organization', 'name': 'Tarvike.com', 'url': `${baseUrl}/` },
    'publisher': { '@type': 'Organization', 'name': 'Tarvike.com', 'logo': { '@type': 'ImageObject', 'url': `${baseUrl}/favicon/favicon-196.png` } }
  }
}
